angular.module('acme.common.service.offline', [
  'acme.common.service.utils'
]).provider('OfflineService', OfflineServiceProvider);

/**
 * Service responsible for managing calls to the offlinesAPI
 * https://acmeticketing.atlassian.net/wiki/spaces/PE/pages/121193900/Offline+Transactions
 * @constructor
 * @param {String} endpoint - the endpoint used
 * @param {Object} $http - angular $http service
 * @param {Function} unwrapAcmeResponse - helper function to unwrap http requests
 * @return {OfflineService} instance
 */
function OfflineService(endpoint, $http, unwrapAcmeResponse) {
  return {
    archive: archive,
    executions: executions,
    details: details,
    find: find,
    list: list,
    rerun: rerun
  };

  /**
   * Finds a particular transaction by uuid
   * @memberOf OfflineService
   * @public
   * @param {String} uuid - Offline transaction's uuid
   * @return {Promise} promise that will resolve or reject with server response
   */
  function find(uuid) {
    return $http.get([endpoint, uuid].join('/'))
      .then(unwrapAcmeResponse);
  }

  /**
   * Calls the archive endpoint
   * @memberOf OfflineService
   * @public
   * @param {String} uuid - Offline transaction's uuid
   * @param {String} reason - Reason to why it should be archived
   * @return {Promise} promise that will resolve or reject with server response
   */
  function archive(uuid, reason) {
    return $http.post([endpoint, uuid, 'archive'].join('/'), {
      reason: reason
    }).then(unwrapAcmeResponse);
  }

  /**
   * @typedef OfflineService.SearchQuery
   * @type {Object}
   * @property {Number} page - the page of results to fetch
   * @property {Number} pageSize - the number of results within one page.
   * @property {String} sortField - the field to sort the results
   * @property {String} sortDirection - the direction of the sort (asc (default) | desc)
   * @property {String} status - the status of the transactions to filter by
   *                                pending | failed | success | executing | archived
   */
  /**
   * Calls the list endpoint
   * @memberOf OfflineService
   * @public
   * @param {OfflineService.SearchQuery=} query - Search Query Object
   * @return {Promise} promise that will resolve or reject with server response
   */
  function list(query) {
    var _query = query || {},
      status = _query.status || '';

    return $http.get(endpoint, {
      params: {
        page: _query.page,
        pageSize: _query.pageSize,
        sortField: _query.sortField,
        sortDirection: _query.sortDirection,
        status: status.toLowerCase() || null
      }
    }).then(unwrapAcmeResponse);
  }

  /**
   * Calls the rerun endpoint
   * @memberOf OfflineService
   * @public
   * @param {Array.<String>|String=} uuidList - List of uuids or a single uuid to be rerun
   *                                        if no array or string is provided all failed
   *                                        transactions will be rerun
   * @return {Promise} promise that will resolve or reject with server response
   */
  function rerun(uuidList) {
    var request = uuidList && uuidList.length ?
      _rerunList(uuidList) :
      _rerunFailed();

    return request
      .then(unwrapAcmeResponse);
  }

  /**
   * Calls the executions endpoint and returns transactions history
   * @memberOf OfflineService
   * @public
   * @param {String} uuid - Offline UUID identifier
   * @return {Promise} promise that will resolve or reject with server response
   */
  function executions(uuid) {
    return $http.get([endpoint, uuid, 'executions'].join('/'))
      .then(unwrapAcmeResponse);
  }

  function details(uuid) {
    return $http.get([endpoint, uuid, 'details'].join('/'))
        .then(unwrapAcmeResponse);
  }

  function _rerunFailed() {
    return $http.post([endpoint, 'rerun', 'failed'].join('/'));
  }

  function _rerunList(uuidList) {
    var _uuidList = [].concat(uuidList);

    return $http.post([endpoint, 'rerun'].join('/'), _uuidList);
  }
}

function OfflineServiceProvider() {}

OfflineServiceProvider.prototype.setEndpoint = function (endpoint) {
  this.endpoint = endpoint;
};

OfflineServiceProvider.prototype.$get = ['$http', 'UtilsService', function ($http, UtilsService) {
  return new OfflineService(this.endpoint, $http, UtilsService.unwrapAcmeResponse);
}];

OfflineServiceProvider.$inject = [];